import { Modal, Table, Button, Group, Text, Center, Loader, Stack, Badge, ActionIcon, Tooltip, Flex, TextInput, CopyButton, Menu, Checkbox } from "@mantine/core";
import { IconCopy, IconCheck, IconSearch, IconPhone, IconMail, IconDownload, IconFilter } from "@tabler/icons-react";
import { useFetch } from "@mantine/hooks";
import { useContext, useMemo, useState } from "react";
import AuthContext from "../../contexts/AuthContext";
import moment from "moment/moment";

export const LEADS_OUTCOME = [
    { value: 'PENDING', label: "Pendiente" },
    { value: 'IN-PROGRESS', label: "En progreso" },
    { value: 'WAITING-FOR-PAYMENT', label: "Esperando pago" },
    { value: 'SUCCESS', label: "Exitoso" },
    { value: 'WARNING', label: "Atención" },
    { value: 'FAILED', label: "Fallido" },
    { value: 'AUTO-FAILED', label: "Fallido automático" },
];

const ALL_OUTCOME_VALUES = LEADS_OUTCOME.map(o => o.value);

const getLeadOutcome = (lead) => {
    if (lead.status === 'CONVERTED') return { value: 'SUCCESS', label: 'Convertido', color: 'green' };
    if (lead.status === 'DISQUALIFIED') return { value: 'FAILED', label: 'Descalificado', color: 'red' };

    const attempts = lead.contactAttempts || [];
    if (attempts.length === 0) return { value: 'PENDING', label: 'Pendiente', color: 'blue' };

    const sorted = [...attempts].sort((a, b) => new Date(b.date) - new Date(a.date));
    const lastOutcome = sorted[0]?.outcome;

    const match = LEADS_OUTCOME.find(o => o.value === lastOutcome);
    const colorMap = {
        'PENDING': 'blue',
        'IN-PROGRESS': 'cyan',
        'WAITING-FOR-PAYMENT': 'orange',
        'SUCCESS': 'green',
        'WARNING': 'yellow',
        'FAILED': 'red',
        'AUTO-FAILED': 'red',
    };

    return {
        value: lastOutcome || 'PENDING',
        label: match?.label || 'Pendiente',
        color: colorMap[lastOutcome] || 'gray',
    };
};

const CopyBtn = ({ value, tooltip, icon: Icon = IconCopy, size = "sm" }) => (
    <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
            <Tooltip label={copied ? "Copiado!" : tooltip} withArrow>
                <ActionIcon color={copied ? "teal" : "gray"} variant="subtle" onClick={copy} size={size}>
                    {copied ? <IconCheck size={14} /> : <Icon size={14} />}
                </ActionIcon>
            </Tooltip>
        )}
    </CopyButton>
);

const STAGE_LABELS = {
    NEW: 'Nuevos', CONTACTED: 'Contactados', IN_PROGRESS: 'En negociación',
    WAITING_PAYMENT: 'Esperando pago', CONVERTED: 'Convertidos', LOST: 'Perdidos',
};
const STAGE_COLORS_MAP = {
    NEW: 'blue', CONTACTED: 'indigo', IN_PROGRESS: 'yellow',
    WAITING_PAYMENT: 'orange', CONVERTED: 'green', LOST: 'red',
};

export default function LeadsListModal({ opened, onClose, filters }) {
    const { authToken } = useContext(AuthContext);
    const [search, setSearch] = useState('');
    // Todos seleccionados por defecto
    const [selectedOutcomes, setSelectedOutcomes] = useState(ALL_OUTCOME_VALUES);

    const leadsUrl = useMemo(() => {
        const base = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-leads`;
        const params = new URLSearchParams();
        if (filters.year) params.set('year', filters.year);
        if (filters.month !== '' && filters.month !== undefined) params.set('month', filters.month);
        if (filters.country) params.set('country', filters.country);
        if (filters.academicUnitId) params.set('unitId', filters.academicUnitId);
        if (filters.funnelStage) params.set('funnelStage', filters.funnelStage);
        return `${base}?${params.toString()}`;
    }, [filters]);

    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
    };

    const { data: leads, loading } = useFetch(opened ? leadsUrl : null, config);

    const filteredLeads = useMemo(() => {
        if (!leads || !Array.isArray(leads)) return [];

        return leads.filter(lead => {
            // Filtro por búsqueda
            if (search.trim()) {
                const s = search.toLowerCase();
                const matchesSearch =
                    (lead.name || '').toLowerCase().includes(s) ||
                    (lead.lastname || '').toLowerCase().includes(s) ||
                    (lead.email || '').toLowerCase().includes(s) ||
                    (lead.phone || '').toLowerCase().includes(s) ||
                    (lead.country || '').toLowerCase().includes(s);
                if (!matchesSearch) return false;
            }

            // Filtro por outcomes seleccionados
            const outcome = getLeadOutcome(lead);
            return selectedOutcomes.includes(outcome.value);
        });
    }, [leads, search, selectedOutcomes]);

    const toggleOutcome = (value) => {
        setSelectedOutcomes(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        );
    };

    const allSelected = selectedOutcomes.length === ALL_OUTCOME_VALUES.length;
    const toggleAll = () => setSelectedOutcomes(allSelected ? [] : ALL_OUTCOME_VALUES);

    const copyLeadInfo = (lead) => {
        const outcome = getLeadOutcome(lead);
        return [
            `Nombre: ${lead.name || ''} ${lead.lastname || ''}`,
            `Email: ${lead.email || ''}`,
            `Teléfono: ${lead.phone || ''}`,
            `País: ${lead.country || ''}`,
            `Estado: ${outcome.label}`,
            `Fecha: ${moment(lead.createdAt).format('DD/MM/YYYY')}`,
        ].join('\n');
    };

    const copyAllAsCSV = () => {
        const header = 'Nombre,Email,Teléfono,País,Estado,Fecha';
        const rows = filteredLeads.map(lead => {
            const outcome = getLeadOutcome(lead);
            return [
                `"${lead.name || ''} ${lead.lastname || ''}"`,
                lead.email || '',
                lead.phone || '',
                lead.country || '',
                outcome.label,
                moment(lead.createdAt).format('DD/MM/YYYY'),
            ].join(',');
        });
        navigator.clipboard.writeText([header, ...rows].join('\n'));
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="80vw"
            title={
                <Flex align="center" gap="sm">
                    <Text fw={700} size="lg">
                        Listado de Leads ({filteredLeads?.length || 0})
                    </Text>
                    {filters.funnelStage && (
                        <Badge color={STAGE_COLORS_MAP[filters.funnelStage] || 'gray'} variant="light">
                            {STAGE_LABELS[filters.funnelStage] || filters.funnelStage}
                        </Badge>
                    )}
                </Flex>
            }
        >
            {loading ? (
                <Center py="xl">
                    <Stack align="center" gap={4}>
                        <Loader size="sm" />
                        <Text size="sm" c="dimmed">Cargando leads...</Text>
                    </Stack>
                </Center>
            ) : (
                <Stack gap="sm">
                    <Flex gap="sm" align="end" wrap="wrap">
                        <TextInput
                            placeholder="Buscar por nombre, email, teléfono..."
                            leftSection={<IconSearch size={16} />}
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            style={{ flex: 1, minWidth: 200 }}
                        />
                        <Group gap="xs">
                            <CopyButton value={filteredLeads.map(l => l.phone).filter(Boolean).join('\n')} timeout={2000}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copiados!" : "Copiar todos los teléfonos"} withArrow>
                                        <Button variant="light" color={copied ? "teal" : "blue"} size="xs"
                                            leftSection={copied ? <IconCheck size={14} /> : <IconPhone size={14} />} onClick={copy}>
                                            Teléfonos
                                        </Button>
                                    </Tooltip>
                                )}
                            </CopyButton>
                            <CopyButton value={filteredLeads.map(l => l.email).filter(Boolean).join('\n')} timeout={2000}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copiados!" : "Copiar todos los emails"} withArrow>
                                        <Button variant="light" color={copied ? "teal" : "blue"} size="xs"
                                            leftSection={copied ? <IconCheck size={14} /> : <IconMail size={14} />} onClick={copy}>
                                            Emails
                                        </Button>
                                    </Tooltip>
                                )}
                            </CopyButton>
                            <Tooltip label="Copiar todo como CSV" withArrow>
                                <Button variant="light" color="gray" size="xs"
                                    leftSection={<IconDownload size={14} />} onClick={copyAllAsCSV}>
                                    CSV
                                </Button>
                            </Tooltip>
                        </Group>
                    </Flex>

                    <div style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders fontSize="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Nombre</Table.Th>
                                    <Table.Th>Email</Table.Th>
                                    <Table.Th>Teléfono</Table.Th>
                                    <Table.Th>País</Table.Th>

                                    {/* ── Columna Estado con filtro ── */}
                                    <Table.Th>
                                        <Menu closeOnItemClick={false} position="bottom-start">
                                            <Menu.Target>
                                                <Flex
                                                    align="center"
                                                    gap={4}
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    <span>Estado</span>
                                                    <IconFilter
                                                        size={14}
                                                        // Resalta el ícono si hay algún filtro activo
                                                        color={allSelected ? 'currentColor' : 'var(--mantine-color-blue-5)'}
                                                    />
                                                </Flex>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                {/* Opción "Todos" */}
                                                <Menu.Item onClick={toggleAll}>
                                                    <Checkbox
                                                        label="Todos"
                                                        checked={allSelected}
                                                        indeterminate={selectedOutcomes.length > 0 && !allSelected}
                                                        onChange={toggleAll}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                </Menu.Item>
                                                <Menu.Divider />
                                                {LEADS_OUTCOME.map(outcome => (
                                                    <Menu.Item
                                                        key={outcome.value}
                                                        onClick={() => toggleOutcome(outcome.value)}
                                                    >
                                                        <Checkbox
                                                            label={outcome.label}
                                                            checked={selectedOutcomes.includes(outcome.value)}
                                                            onChange={() => toggleOutcome(outcome.value)}
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                    </Menu.Item>
                                                ))}
                                            </Menu.Dropdown>
                                        </Menu>
                                    </Table.Th>

                                    <Table.Th>Fecha</Table.Th>
                                    <Table.Th style={{ width: 50 }}></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {filteredLeads.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text ta="center" c="dimmed" py="md">
                                                No se encontraron leads para los filtros seleccionados.
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    filteredLeads.map((lead) => {
                                        const outcome = getLeadOutcome(lead);
                                        return (
                                            <Table.Tr key={lead._id}>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {lead.name || ''} {lead.lastname || ''}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4} wrap="nowrap">
                                                        <Text size="sm" style={{ wordBreak: 'break-all' }}>
                                                            {lead.email || '-'}
                                                        </Text>
                                                        {lead.email && <CopyBtn value={lead.email} tooltip="Copiar email" icon={IconMail} />}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4} wrap="nowrap">
                                                        <Text size="sm">{lead.phone || '-'}</Text>
                                                        {lead.phone && <CopyBtn value={lead.phone} tooltip="Copiar teléfono" icon={IconPhone} />}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{lead.country || '-'}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={outcome.color} variant="light" size="sm">
                                                        {outcome.label}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {moment(
                                                            lead.relevantAttempts?.length > 0
                                                                ? lead.relevantAttempts[lead.relevantAttempts.length - 1].date
                                                                : lead.createdAt
                                                        ).format('DD/MM/YYYY')}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <CopyBtn value={copyLeadInfo(lead)} tooltip="Copiar toda la info" icon={IconCopy} />
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })
                                )}
                            </Table.Tbody>
                        </Table>
                    </div>

                    {filteredLeads.length > 0 && (
                        <Text size="xs" c="dimmed" ta="right">
                            Mostrando {filteredLeads.length} de {leads?.length || 0} leads
                        </Text>
                    )}
                </Stack>
            )}
        </Modal>
    );
}